import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import {
  Button,
  InlineNotification,
  SkeletonText,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@carbon/react";
import AuthLayout from "../../../components/carbon/AuthLayout/AuthLayout";
import { isOcConflict } from "../../../hooks/useOcActions";
import { isOcNotFound } from "../../../hooks/useRespondOc";
import {
  useOcTokenDetails,
  useRespondOcToken,
} from "../../../hooks/useOcToken";
import { PurchaseOrderTokenSummary } from "../../../types";
import { formatDate } from "../../../utils/utils";
import OcStatusPill from "../components/OcStatusPill";
import { formatOrderTotal } from "../formatOrderTotal";
import RejectSupplierOcModal from "../Supplier/RejectSupplierOcModal";
import "./tokenAccept.scss";

type Outcome = "accepted" | "rejected" | "invalid" | "alreadyResponded";

const Fact = ({ label, value }: { label: string; value: string | null }) => (
  <div className="token-accept__fact">
    <dt>{label}</dt>
    <dd>{value || "-"}</dd>
  </div>
);

/**
 * PUBLIC, no-login OC acceptance page (`/purchase-orders/token/:token`, the link in the emailed OC).
 * Renders the single OC the token addresses - nothing else - with Accept / Reject (reject needs a
 * reason). An unknown / expired / spent token (404 / 409 from details or respond) is an explicit
 * friendly state, never a crash. It requires no auth or user context.
 */
const TokenAcceptPage = () => {
  const { t } = useTranslation();
  const { token } = useParams<{ token: string }>();
  const details = useOcTokenDetails(token);
  const respond = useRespondOcToken();
  const [outcome, setOutcome] = useState<Outcome | null>(null);
  const [rejecting, setRejecting] = useState(false);
  const [responseFailed, setResponseFailed] = useState(false);

  const send = (accept: boolean, reason?: string) => {
    if (!token) return;
    setResponseFailed(false);
    respond.mutate(
      { token, accept, reason },
      {
        onSuccess: () => {
          setRejecting(false);
          setOutcome(accept ? "accepted" : "rejected");
        },
        onError: (error) => {
          if (isOcNotFound(error)) {
            setRejecting(false);
            setOutcome("invalid");
          } else if (isOcConflict(error)) {
            setRejecting(false);
            setOutcome("alreadyResponded");
          } else {
            setResponseFailed(true);
          }
        },
      }
    );
  };

  const stateFromDetailsError = (): Outcome | "error" => {
    if (isOcNotFound(details.error)) return "invalid";
    if (isOcConflict(details.error)) return "alreadyResponded";
    return "error";
  };

  const state: Outcome | "error" | null =
    outcome ?? (details.isError ? stateFromDetailsError() : null);

  const notice = (
    kind: "success" | "error" | "info",
    title: string,
    subtitle: string
  ) => (
    <InlineNotification
      kind={kind}
      lowContrast
      hideCloseButton
      title={title}
      subtitle={subtitle}
    />
  );

  const renderState = (s: Outcome | "error") => {
    switch (s) {
      case "accepted":
        return notice(
          "success",
          t("purchaseOrders.token.accepted.title"),
          t("purchaseOrders.token.accepted.message")
        );
      case "rejected":
        return notice(
          "success",
          t("purchaseOrders.token.rejected.title"),
          t("purchaseOrders.token.rejected.message")
        );
      case "alreadyResponded":
        return notice(
          "info",
          t("purchaseOrders.token.alreadyResponded.title"),
          t("purchaseOrders.token.alreadyResponded.message")
        );
      case "invalid":
        return notice(
          "error",
          t("purchaseOrders.token.invalid.title"),
          t("purchaseOrders.token.invalid.message")
        );
      default:
        return notice(
          "error",
          t("purchaseOrders.token.error.title"),
          t("purchaseOrders.token.error.message")
        );
    }
  };

  const renderSummary = (oc: PurchaseOrderTokenSummary) => (
    <>
      <div className="token-accept__header">
        <h1 className="token-accept__title">
          {t("purchaseOrders.token.title", { code: oc.code })}
        </h1>
        <OcStatusPill status={oc.status} />
      </div>
      <dl className="token-accept__facts">
        <Fact
          label={t("purchaseOrders.token.buyer")}
          value={oc.buyerCompanyName}
        />
        <Fact
          label={t("purchaseOrders.token.supplier")}
          value={oc.supplierName}
        />
        <Fact
          label={t("purchaseOrders.token.requestedDelivery")}
          value={
            oc.requestedDeliveryDate
              ? formatDate(oc.requestedDeliveryDate)
              : null
          }
        />
        <Fact
          label={t("purchaseOrders.token.deliverySite")}
          value={
            [oc.deliverySiteName, oc.deliverySiteAddress]
              .filter(Boolean)
              .join(" - ") || null
          }
        />
        <Fact
          label={t("purchaseOrders.token.paymentTerm")}
          value={oc.paymentTerm}
        />
        <Fact
          label={t("purchaseOrders.token.paymentMethod")}
          value={oc.paymentMethod}
        />
        <Fact label={t("purchaseOrders.token.incoterm")} value={oc.incoterm} />
        <Fact label={t("purchaseOrders.token.notes")} value={oc.notes} />
        <div className="token-accept__fact">
          <dt>{t("purchaseOrders.token.total")}</dt>
          <dd className="token-accept__total">
            {formatOrderTotal(oc.totalAmount, oc.currency)}
          </dd>
        </div>
      </dl>
      <Table size="md" aria-label={t("purchaseOrders.token.lines.label")}>
        <TableHead>
          <TableRow>
            <TableHeader>
              {t("purchaseOrders.token.lines.material")}
            </TableHeader>
            <TableHeader>
              {t("purchaseOrders.token.lines.quantity")}
            </TableHeader>
            <TableHeader>
              {t("purchaseOrders.token.lines.unitPrice")}
            </TableHeader>
            <TableHeader>{t("purchaseOrders.token.lines.total")}</TableHeader>
          </TableRow>
        </TableHead>
        <TableBody>
          {oc.lines.map((line, index) => (
            <TableRow key={index}>
              <TableCell>
                {line.materialName || line.description || "-"}
              </TableCell>
              <TableCell>
                {line.quantity} {line.unit ?? ""}
              </TableCell>
              <TableCell>
                {formatOrderTotal(line.unitPrice, oc.currency)}
              </TableCell>
              <TableCell>
                {formatOrderTotal(line.lineTotal, oc.currency)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {oc.actionable ? (
        <>
          {responseFailed &&
            notice(
              "error",
              t("purchaseOrders.token.error.title"),
              t("purchaseOrders.token.respondError")
            )}
          <div className="token-accept__actions">
            <Button disabled={respond.isPending} onClick={() => send(true)}>
              {t("purchaseOrders.token.accept")}
            </Button>
            <Button
              kind="danger--tertiary"
              disabled={respond.isPending}
              onClick={() => setRejecting(true)}
            >
              {t("purchaseOrders.token.reject")}
            </Button>
          </div>
          <RejectSupplierOcModal
            open={rejecting}
            code={oc.code}
            isPending={respond.isPending}
            onClose={() => setRejecting(false)}
            onSubmit={(reason) => send(false, reason)}
          />
        </>
      ) : (
        notice(
          "info",
          t("purchaseOrders.token.alreadyResponded.title"),
          t("purchaseOrders.token.alreadyResponded.message")
        )
      )}
    </>
  );

  return (
    <AuthLayout width="wide">
      <div className="token-accept">
        {state ? (
          renderState(state)
        ) : details.isLoading || !details.data ? (
          <SkeletonText
            paragraph
            lineCount={6}
            aria-label={t("purchaseOrders.token.loading")}
          />
        ) : (
          renderSummary(details.data)
        )}
      </div>
    </AuthLayout>
  );
};

export default TokenAcceptPage;
